import {
  buildTruckParkingCityPath,
  buildTruckParkingIntentPath,
  buildTruckParkingStatePath,
  getCityDiscoveryRecord,
  getIntentDiscoveryRecord,
  getStateDiscoveryRecord,
} from './truckParkingDiscovery';

const pluralize = (count = 0, singular = '', plural = '') => {
  const safePlural = plural || `${singular}s`;
  return `${count} ${count === 1 ? singular : safePlural}`;
};

const verbForCount = (count = 0, singular = 'is', plural = 'are') => (
  count === 1 ? singular : plural
);

const formatList = (items = []) => {
  const safeItems = (Array.isArray(items) ? items : []).filter(Boolean);
  if (safeItems.length === 0) return '';
  if (safeItems.length === 1) return safeItems[0];
  if (safeItems.length === 2) return `${safeItems[0]} and ${safeItems[1]}`;
  return `${safeItems.slice(0, -1).join(', ')}, and ${safeItems[safeItems.length - 1]}`;
};

const uniqueBy = (items = [], limit = 5) => {
  const seen = new Set();
  return (Array.isArray(items) ? items : [])
    .map((item) => String(item || '').trim())
    .filter(Boolean)
    .filter((item) => {
      const normalized = item.toLowerCase();
      if (seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    })
    .slice(0, limit);
};

const countDatasetCities = (dataset = null) => (
  (dataset?.states || []).reduce((total, state) => total + (state?.cities?.length || 0), 0)
);

const countLocationsWithIntent = (locations = [], intentSlug = '') => (
  (Array.isArray(locations) ? locations : []).filter((location) => (
    (location?.allIntentEntries || []).some((entry) => entry.intentSlug === intentSlug)
  )).length
);

const sortFeaturedLocations = (locations = [], limit = 6) => (
  [...(Array.isArray(locations) ? locations : [])]
    .sort((left, right) => {
      const intentDelta = (right?.allIntentEntries?.length || 0) - (left?.allIntentEntries?.length || 0);
      if (intentDelta !== 0) return intentDelta;
      const cityDelta = String(left?.cityName || '').localeCompare(String(right?.cityName || ''));
      if (cityDelta !== 0) return cityDelta;
      return String(left?.title || left?.address || left?.id || '').localeCompare(
        String(right?.title || right?.address || right?.id || '')
      );
    })
    .slice(0, limit)
);

const buildLinkSection = (title = '', items = [], emptyCopy = '') => ({
  kind: 'links',
  title,
  items: (Array.isArray(items) ? items : []).filter((item) => item?.path && item?.label),
  emptyCopy,
});

const buildBulletSection = (title = '', items = []) => ({
  kind: 'bullets',
  title,
  items: (Array.isArray(items) ? items : []).filter(Boolean),
});

const buildFaqSection = (title = '', items = []) => ({
  kind: 'faq',
  title,
  items: (Array.isArray(items) ? items : []).filter((item) => item?.question && item?.answer),
});

const buildParagraphSection = (title = '', items = []) => ({
  kind: 'paragraphs',
  title,
  items: (Array.isArray(items) ? items : []).filter(Boolean),
});

const CITY_LOCAL_CONTEXT = {
  charlotte: {
    title: 'Charlotte Truck Parking — Highway Corridors and Freight Context',
    paragraphs: [
      'Charlotte sits at the intersection of I-77 and I-85, with I-485 forming the outer belt — one of the busiest freight corridors in the Southeast. Drivers stop here as a freight destination for deliveries to distribution terminals along North Graham Street and the industrial zones near Kinder Morgan, Magellan, and Marathon. The I-77/I-85 split near Brookshire Freeway is a known chokepoint, and HOS windows tighten quickly for drivers who hit Charlotte after a full day on I-85 from Atlanta or Richmond.',
      'Truck Parking Hub lots are positioned on Raleigh Street, Robinson Circle, and Rozzelles Ferry Road — all within five miles of key I-77 and I-85 interchanges — giving drivers a compliant overnight stop without adding extra mileage after a long haul.',
    ],
  },
  matthews: {
    title: 'Matthews Truck Parking — I-485 and East Charlotte Freight',
    paragraphs: [
      'Matthews is a suburb southeast of Charlotte with direct access to I-485 and NC-74 (Independence Blvd), making it a natural layover for drivers finishing a run to east Charlotte distribution centers or staging for a morning delivery push into the city core. The area serves drivers who need overnight security within easy reach of the southeast Charlotte freight zone without parking in the congested city center.',
      "Truck Parking Hub's Matthews lot is positioned for quick I-485 re-entry, keeping drivers on schedule without losing time searching for a safe stop after dark.",
    ],
  },
  'mint-hill': {
    title: 'Mint Hill Truck Parking — I-485 East and US-74 Corridor',
    paragraphs: [
      'Mint Hill sits on the eastern edge of the Charlotte metro where I-485 and US-74 intersect, serving as a pass-through corridor for drivers coming off US-74 from Monroe and Wingate or moving from Charlotte toward the I-85 south ramp. Industrial parks and distribution facilities in the Mint Hill and Stallings area generate consistent freight traffic on this corridor.',
      "Truck Parking Hub's Mint Hill lot gives drivers overnight security on the east side of the Charlotte metro without backtracking into the city to find a compliant stop.",
    ],
  },
  columbus: {
    title: 'Columbus Truck Parking — I-71 Corridor and Central Ohio Freight',
    paragraphs: [
      "Columbus is the freight crossroads of Ohio, where I-71 runs north-south and I-70 runs east-west, connecting Cleveland, Cincinnati, Pittsburgh, and Indianapolis. Truck Parking Hub's lot near Watkins Road off I-71 serves drivers who are mid-run on the Cleveland-to-Cincinnati lane or finishing a haul from Pittsburgh or Indianapolis. The facility is near Central Aluminum, DCM 1, Huttig Building Products, and LifeLine Mobile distribution centers in the southeast Columbus industrial zone.",
      'Columbus HOS windows tighten fast — I-670 and inner-belt traffic can burn an hour during peak periods, making early parking decisions critical for compliance.',
    ],
  },
  carnegie: {
    title: 'Carnegie Truck Parking — I-79 / Pittsburgh Corridor',
    paragraphs: [
      "Carnegie sits just southwest of Pittsburgh at the I-79 and US-19 junction, serving as the last reliable parking point for drivers approaching Pittsburgh from the south before Fort Pitt Tunnel traffic makes progress unpredictable. It is used primarily by pass-through drivers on the I-79 corridor and by drivers making final-mile deliveries into Pittsburgh's South Side or Southpointe business park.",
      "Drivers on eastern US freight lanes — particularly I-76 to I-79 — rely on Carnegie for overnight stops that avoid the difficulty of finding compliant parking inside the city.",
    ],
  },
  ellenwood: {
    title: 'Ellenwood Truck Parking — I-285 and Atlanta Metro Southeast',
    paragraphs: [
      'Ellenwood sits southeast of Atlanta near I-285 and I-675, serving drivers who need to park outside the Atlanta perimeter before or after making deliveries into the metro. The Campbell Blvd industrial corridor runs parallel to I-285 and hosts several distribution centers, making Ellenwood a freight destination for last-mile deliveries into southeast Atlanta.',
      'HOS management is critical here: I-285 traffic regularly backs up from exit 52 to exit 60, and drivers who underestimate the Atlanta loop can burn two to three hours of drive time in congestion. Parking in Ellenwood keeps drivers compliant and positioned for a clean metro entry the next morning.',
    ],
  },
  cartersville: {
    title: 'Cartersville Truck Parking — I-75 North Georgia Staging',
    paragraphs: [
      'Cartersville is a freight corridor city on I-75, approximately 45 miles north of Atlanta, serving drivers moving between Tennessee and Atlanta on one of the highest-volume freight interstates in the South. The area near Five Forks Road hosts industrial facilities and serves as a staging point for drivers who need to time their entry into Atlanta during off-peak hours.',
      'Drivers on the Chattanooga-to-Atlanta lane frequently stop here for overnight parking to avoid arriving at metro Atlanta during peak hours — protecting both HOS compliance and delivery timing on tight schedules.',
    ],
  },
};

const CITY_CORRIDOR_LINKS = {
  charlotte: ['matthews', 'mint-hill'],
  matthews: ['charlotte', 'mint-hill'],
  'mint-hill': ['charlotte', 'matthews'],
  ellenwood: ['cartersville'],
  cartersville: ['ellenwood'],
};

// City → highway corridor page links. Keyed by citySlug.
const CITY_HIGHWAY_LINKS = {
  charlotte: [
    { slug: 'near-i-77', label: 'Near I-77' },
    { slug: 'near-i-85', label: 'Near I-85' },
    { slug: 'near-i-485', label: 'Near I-485' },
    { slug: 'near-us-29', label: 'Near US-29' },
  ],
  matthews: [{ slug: 'near-i-485', label: 'Near I-485' }],
  'mint-hill': [{ slug: 'near-i-485', label: 'Near I-485' }],
  columbus: [{ slug: 'near-i-71', label: 'Near I-71' }],
  carnegie: [{ slug: 'near-i-279', label: 'Near I-279' }],
  ellenwood: [
    { slug: 'near-i-285', label: 'Near I-285' },
    { slug: 'near-i-85', label: 'Near I-85' },
    { slug: 'near-i-75-georgia', label: 'Near I-75 (Georgia)' },
  ],
  cartersville: [
    { slug: 'near-i-75-georgia', label: 'Near I-75 (Georgia)' },
    { slug: 'near-i-285', label: 'Near I-285' },
  ],
};

// Per-city FAQ overrides — substantive content replacing generic navigation answers.
const CITY_FAQ_OVERRIDES = {
  charlotte: [
    {
      question: 'Can I park a 53-foot trailer overnight in Charlotte?',
      answer: 'Yes. All Charlotte Truck Parking Hub locations accommodate 53-foot trailers with standard clearance. Overnight parking is available at daily ($27) or weekly ($180) rates. Check live availability and book from the Charlotte page.',
    },
    {
      question: 'What are the access hours at Charlotte parking lots?',
      answer: 'All Charlotte locations operate 24/7 with gated access. Your booking confirmation includes the gate code — there is no staffed check-in window and no after-hours surcharge.',
    },
    {
      question: 'How close are these lots to I-77 and I-85?',
      answer: 'Charlotte lots on Raleigh Street, Robinson Circle, and Rozzelles Ferry Road are within five miles of key I-77 and I-85 interchanges. The Rozzelles Ferry location is closest to the I-77/I-85 junction.',
    },
    {
      question: 'What cancellation policy applies?',
      answer: 'Cancellation and refund terms are displayed at checkout before you confirm. Review the booking terms on the lot detail page or contact Truck Parking Hub directly for questions about your reservation.',
    },
  ],
  matthews: [
    {
      question: 'Can I park a 53-foot trailer at the Matthews lot?',
      answer: 'Yes. The Matthews location accommodates 53-foot trailers and is positioned near I-485 and NC-74 for quick re-entry after an overnight stop.',
    },
    {
      question: 'What access hours apply in Matthews?',
      answer: '24/7 gated access. A gate code is delivered in your booking confirmation — no staffed check-in or call-ahead required.',
    },
    {
      question: 'What rates are available at the Matthews location?',
      answer: 'Daily, weekly, and monthly rates are available. Monthly rates offer the best per-night cost for drivers who regularly pass through the east Charlotte corridor on I-485.',
    },
  ],
  'mint-hill': [
    {
      question: 'Does the Mint Hill lot accept 53-foot trailers?',
      answer: 'Yes. The Mint Hill location handles standard 53-foot trailers near I-485 and US-74 on the east side of the Charlotte metro.',
    },
    {
      question: 'What access is available at the Mint Hill lot?',
      answer: '24/7 gated access. Book online and receive your gate code in your booking confirmation — no call-ahead required.',
    },
    {
      question: 'Who typically uses the Mint Hill parking location?',
      answer: "Primarily drivers coming off US-74 from Monroe and Wingate, or staging on I-485 east before a morning Charlotte delivery — avoiding the congested city center for an overnight stop.",
    },
  ],
  columbus: [
    {
      question: 'Where exactly is the Columbus truck parking lot?',
      answer: 'Near Watkins Road, just off I-71 Exit 101 in southeast Columbus — close to Central Aluminum, DCM 1, Huttig Building Products, and LifeLine Mobile distribution centers in the southeast Columbus industrial zone.',
    },
    {
      question: 'What trailer sizes are accepted in Columbus?',
      answer: '53-foot trailers are supported. Confirm specific clearance details on the lot page before booking.',
    },
    {
      question: 'Is Columbus parking available for daily and monthly stays?',
      answer: 'Yes. Daily, weekly, and monthly rates are available. The location serves both pass-through drivers on the I-71 corridor and regulars on the Columbus-area distribution run.',
    },
  ],
  carnegie: [
    {
      question: 'Is Carnegie the best parking option near Pittsburgh?',
      answer: 'For drivers approaching from the south on I-79 or I-76 PA Turnpike, Carnegie is the primary compliant parking option before the Pittsburgh city approach — positioned near I-79 and US-19, before Fort Pitt Tunnel traffic becomes unpredictable.',
    },
    {
      question: 'What trailer sizes are accepted in Carnegie?',
      answer: '53-foot trailers are supported. Review lot dimensions on the booking page before arrival.',
    },
    {
      question: 'What rates and access are available in Carnegie?',
      answer: 'Daily, weekly, and monthly rates. Book online — gate code is in your booking confirmation. 24/7 access, no staffed check-in required.',
    },
  ],
  ellenwood: [
    {
      question: 'How close is Ellenwood to the Atlanta perimeter?',
      answer: 'Ellenwood is positioned near I-285 and I-675 on the southeast side of the Atlanta perimeter, near Exit 52-55 on I-285 — the standard staging point for drivers managing metro Atlanta deliveries from the south.',
    },
    {
      question: 'What trailer sizes are supported in Ellenwood?',
      answer: '53-foot trailers are supported. The Ellenwood lot handles standard commercial trailer combinations.',
    },
    {
      question: 'Why park in Ellenwood instead of inside Atlanta?',
      answer: 'Compliant truck parking inside Atlanta is difficult to find, and I-285 traffic regularly backs up two to three hours during peak periods. Ellenwood keeps drivers compliant and positioned for a clean metro entry the next morning.',
    },
  ],
  cartersville: [
    {
      question: 'Why stop in Cartersville instead of driving directly to Atlanta?',
      answer: 'Cartersville is approximately 45 miles north of Atlanta on I-75. Drivers stop here to time their Atlanta arrival during off-peak hours — protecting both HOS compliance and on-time delivery. I-75 approaching Atlanta can back up two to three hours during peak periods.',
    },
    {
      question: 'What trailer sizes are accepted in Cartersville?',
      answer: '53-foot trailers are supported. Daily rates start from $14.',
    },
    {
      question: 'What access hours apply at the Cartersville lot?',
      answer: '24/7 gated access. Book online and your gate code is included in your booking confirmation. No staffed check-in window.',
    },
  ],
};

// Per-state FAQ overrides.
const STATE_FAQ_OVERRIDES = {
  'north-carolina': [
    {
      question: 'What truck parking is available in North Carolina?',
      answer: 'Truck Parking Hub operates 5 truck parking locations in North Carolina across Charlotte (3 lots), Matthews, and Mint Hill. All lots are gated with 24/7 access. Daily rates start from $27, weekly from $180.',
    },
    {
      question: 'Which NC lots are closest to I-77 and I-85?',
      answer: 'Charlotte lots on Raleigh Street, Robinson Circle, and Rozzelles Ferry Road are within five miles of key I-77 and I-85 interchanges. Matthews and Mint Hill serve the I-485 outer belt on the east side of the metro.',
    },
  ],
  georgia: [
    {
      question: 'What truck parking does Truck Parking Hub offer in Georgia?',
      answer: 'Two locations in Georgia: Ellenwood near I-285 and I-675 on the southeast Atlanta perimeter, and NE Cartersville on I-75 approximately 45 miles north of Atlanta. Both offer daily, weekly, and monthly parking.',
    },
    {
      question: 'Which Georgia lot should I use for Atlanta-area deliveries?',
      answer: 'Ellenwood is the primary stop for southeast metro deliveries near I-285. Cartersville is used for staging northbound I-75 runs or timing Atlanta entries from the north to avoid peak traffic.',
    },
  ],
  ohio: [
    {
      question: 'Where is Truck Parking Hub truck parking located in Ohio?',
      answer: 'Columbus, OH near Watkins Road off I-71 Exit 101. The lot serves drivers on the Cleveland-to-Cincinnati corridor and local distribution runs near Central Aluminum, Huttig Building Products, and LifeLine Mobile in the southeast Columbus industrial zone.',
    },
    {
      question: 'What rates are available at the Columbus, OH location?',
      answer: 'Daily, weekly, and monthly rates. Book online 24/7 — gate code is included in your booking confirmation.',
    },
  ],
  pennsylvania: [
    {
      question: 'Where is Truck Parking Hub truck parking located in Pennsylvania?',
      answer: 'Carnegie, PA near Pittsburgh — at the I-79/US-19 junction. It is the primary compliant stop before Fort Pitt Tunnel traffic becomes unpredictable for drivers on the I-76 PA Turnpike to I-79 lane.',
    },
    {
      question: 'What rates are available in Carnegie, PA?',
      answer: 'Daily, weekly, and monthly rates available. 24/7 gated access — gate code delivered with your booking confirmation.',
    },
  ],
};

const collectTopHighways = (locations = [], limit = 4) => {
  const counts = new Map();
  (Array.isArray(locations) ? locations : []).forEach((location) => {
    (location?.profile?.highways || []).forEach((highway) => {
      const name = String(highway?.name || '').trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
  });

  return [...counts.entries()]
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    })
    .slice(0, limit)
    .map(([name]) => name);
};

const collectTopFreightHotspots = (locations = [], limit = 4) => {
  const counts = new Map();
  (Array.isArray(locations) ? locations : []).forEach((location) => {
    (location?.profile?.freightHotspots || []).forEach((hotspot) => {
      const name = String(hotspot?.name || '').trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
  });

  return [...counts.entries()]
    .sort((left, right) => {
      if (right[1] !== left[1]) return right[1] - left[1];
      return left[0].localeCompare(right[0]);
    })
    .slice(0, limit)
    .map(([name]) => name);
};

const collectTopLabels = (locations = [], selector = () => [], limit = 5) => uniqueBy(
  (Array.isArray(locations) ? locations : []).flatMap((location) => selector(location) || []),
  limit,
);

const collectLotFaqs = (locations = [], limit = 6) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(locations) ? locations : []).forEach((location) => {
    (location?.profile?.faq || []).forEach((item) => {
      if (!item?.question || !item?.answer) return;
      const key = item.question.toLowerCase().trim();
      if (seen.has(key)) return;
      seen.add(key);
      result.push(item);
    });
  });
  return result.slice(0, limit);
};

const buildHomepageStateLinks = (dataset = null) => (
  [...(dataset?.states || [])]
    .filter((state) => (state?.cities?.length || 0) > 0)
    .sort((left, right) => {
      if ((right?.locations?.length || 0) !== (left?.locations?.length || 0)) {
        return (right?.locations?.length || 0) - (left?.locations?.length || 0);
      }
      return String(left?.stateName || '').localeCompare(String(right?.stateName || ''));
    })
    .map((state) => ({
      path: buildTruckParkingStatePath(state.stateSlug),
      label: state.stateName,
      meta: `${pluralize(state.cities.length, 'city', 'cities')} · ${pluralize(state.locations.length, 'location')}`,
    }))
);

const buildPopularCityLinks = (dataset = null, limit = 8) => (
  [...(dataset?.states || [])]
    .flatMap((state) => (state.cities || []).map((city) => ({
      path: buildTruckParkingCityPath({ stateSlug: state.stateSlug, citySlug: city.citySlug }),
      label: `${city.cityName}, ${state.stateCode}`,
      meta: `${pluralize(city.locations.length, 'location')} · ${pluralize(city.availableIntentSlugs.length, 'filter')}`,
      count: city.locations.length,
    })))
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return left.label.localeCompare(right.label);
    })
    .slice(0, limit)
);

const buildStateIntentLinks = (stateRecord = null, limit = 6) => {
  if (!stateRecord) return [];

  return stateRecord.cities
    .flatMap((city) => [
      ...city.fixedIntents.map((intent) => ({
        path: buildTruckParkingIntentPath({
          stateSlug: stateRecord.stateSlug,
          citySlug: city.citySlug,
          intentSlug: intent.intentSlug,
        }),
        label: `${intent.label} in ${city.cityName}`,
        meta: `${pluralize(intent.count, 'matching lot')}`,
        count: intent.count,
      })),
      ...city.corridorIntents.map((intent) => ({
        path: buildTruckParkingIntentPath({
          stateSlug: stateRecord.stateSlug,
          citySlug: city.citySlug,
          intentSlug: intent.intentSlug,
        }),
        label: `${intent.label} in ${city.cityName}`,
        meta: `${pluralize(intent.count, 'matching lot')}`,
        count: intent.count,
      })),
    ])
    .sort((left, right) => {
      if (right.count !== left.count) return right.count - left.count;
      return left.label.localeCompare(right.label);
    })
    .slice(0, limit);
};

const buildCityIntentLinks = (cityRecord = null) => {
  if (!cityRecord) return [];

  return [
    ...cityRecord.fixedIntents.map((intent) => ({
      path: buildTruckParkingIntentPath({
        stateSlug: cityRecord.stateSlug,
        citySlug: cityRecord.citySlug,
        intentSlug: intent.intentSlug,
      }),
      label: intent.label,
      meta: `${pluralize(intent.count, 'matching lot')}`,
    })),
    ...cityRecord.corridorIntents.map((intent) => ({
      path: buildTruckParkingIntentPath({
        stateSlug: cityRecord.stateSlug,
        citySlug: cityRecord.citySlug,
        intentSlug: intent.intentSlug,
      }),
      label: intent.label,
      meta: `${pluralize(intent.count, 'matching lot')}`,
    })),
  ];
};

const buildIntentSiblingLinks = (intentRecord = null) => {
  const cityRecord = intentRecord?.city;
  if (!cityRecord) return [];

  return buildCityIntentLinks(cityRecord)
    .filter((item) => !item.path.endsWith(`/${intentRecord.intentSlug}`));
};

const buildFeaturedLocationItems = (locations = [], limit = 6) => sortFeaturedLocations(locations, limit).map((location) => ({
  path: location.pricingPath,
  label: location.title || location.address || location.id,
  meta: `${location.cityName}, ${location.stateCode}${location.profile?.areaLabel ? ` · ${location.profile.areaLabel}` : ''}`,
  location,
}));

const buildHomepageContent = ({ dataset = null } = {}) => {
  const states = dataset?.states || [];
  if (states.length === 0) return null;

  const locations = states.flatMap((state) => state.locations || []);
  return {
    routeKind: 'homepage',
    heading: 'Truck Parking Hub',
    sectionHeading: 'Search Parking by State',
    intro: 'Browse by state, open the city that fits your route, and compare parking locations before you book.',
    statItems: [
      pluralize(states.length, 'state'),
      pluralize(countDatasetCities(dataset), 'city', 'cities'),
      pluralize(locations.length, 'parking location'),
    ],
    sections: [
      buildLinkSection('Search Parking by State', buildHomepageStateLinks(dataset)),
      buildLinkSection('Popular Cities', buildPopularCityLinks(dataset, 6)),
    ],
    featuredLocationsTitle: 'Featured Truck Parking Locations',
    featuredLocations: buildFeaturedLocationItems(locations, 6),
    faqItems: [],
  };
};

const buildHubContent = ({ dataset = null } = {}) => {
  const states = dataset?.states || [];
  if (states.length === 0) return null;

  const locations = states.flatMap((state) => state.locations || []);
  return {
    routeKind: 'hub',
    heading: 'Truck Parking by State and City',
    sectionHeading: 'Browse by State and City',
    intro: 'Choose your state, open the city you need, and then pick the lot that fits your stop.',
    statItems: [
      pluralize(states.length, 'state'),
      pluralize(countDatasetCities(dataset), 'city', 'cities'),
      pluralize(locations.length, 'parking location'),
    ],
    sections: [
      buildBulletSection('Start Here', [
        `You can browse ${pluralize(locations.length, 'parking location')} across ${pluralize(countDatasetCities(dataset), 'city', 'cities')} and ${pluralize(states.length, 'state')}.`,
        'Pick your state first, then open the city page that matches your route or delivery area.',
        'If you need a shorter list, use Overnight, Secure, Monthly, or Near Highway links.',
      ]),
      buildLinkSection('States We Serve', buildHomepageStateLinks(dataset)),
      buildLinkSection('Popular Cities', buildPopularCityLinks(dataset, 8)),
    ],
    featuredLocationsTitle: 'Featured Parking Locations',
    featuredLocations: buildFeaturedLocationItems(locations, 6),
    faqItems: [],
  };
};

const buildStateContent = ({ dataset = null, routeMeta = null } = {}) => {
  const stateRecord = getStateDiscoveryRecord(dataset, routeMeta?.stateSlug || '');
  if (!stateRecord || stateRecord.cities.length === 0) return null;

  const topCityLabels = stateRecord.cities.slice(0, 3).map((city) => city.cityName);
  const highways = collectTopHighways(stateRecord.locations, 4);
  const freightHotspots = collectTopFreightHotspots(stateRecord.locations, 4);
  const overnightCount = countLocationsWithIntent(stateRecord.locations, 'overnight');
  const secureCount = countLocationsWithIntent(stateRecord.locations, 'secure');
  const monthlyCount = countLocationsWithIntent(stateRecord.locations, 'monthly');
  const locationCount = stateRecord.locations.length;
  const cityCount = stateRecord.cities.length;
  const isSingle = locationCount === 1;

  const intro = isSingle
    ? `${pluralize(locationCount, 'parking location')} ${verbForCount(locationCount)} active in ${stateRecord.stateName}. Open the lot page to view rates, amenities, and booking options.`
    : `See which ${cityCount === 1 ? 'city' : 'cities'} in ${stateRecord.stateName} ${verbForCount(cityCount, 'has', 'have')} parking, then open the ${cityCount === 1 ? 'city' : 'city that best fits your route'}.`;

  const countBullet = isSingle
    ? `${pluralize(locationCount, 'parking location')} ${verbForCount(locationCount)} active in ${stateRecord.stateName} in ${topCityLabels[0] || 'this area'}.`
    : `${pluralize(locationCount, 'parking location')} ${verbForCount(locationCount)} listed across ${pluralize(cityCount, 'city', 'cities')} in ${stateRecord.stateName}.`;

  const shortcutBullet = isSingle
    ? null
    : `Shortcut pages are available for ${pluralize(overnightCount, 'overnight option')}, ${pluralize(secureCount, 'secure option')}, and ${pluralize(monthlyCount, 'monthly option')} when those lots are available.`;

  const cityBullet = !isSingle && topCityLabels.length > 0
    ? `If you want to start with the busiest areas, check ${formatList(topCityLabels)}.`
    : null;

  const faqAnswer = isSingle
    ? `${pluralize(locationCount, 'parking location')} ${verbForCount(locationCount)} active in ${stateRecord.stateName}. Open the lot page directly to view rates, amenities, and book your spot.`
    : `This page shows ${pluralize(locationCount, 'parking location')} across ${pluralize(cityCount, 'city', 'cities')} in ${stateRecord.stateName}${cityCount > 1 ? ', so you can pick a city first and then compare the lots that fit your trip' : ''}.`;

  return {
    routeKind: 'state',
    heading: `Truck Parking in ${stateRecord.stateName}`,
    sectionHeading: `${stateRecord.stateName} Parking Locations`,
    intro,
    statItems: [
      pluralize(cityCount, 'city', 'cities'),
      pluralize(locationCount, 'parking location'),
      pluralize(buildStateIntentLinks(stateRecord, 99).length, 'quick link'),
    ],
    sections: [
      buildBulletSection(`How many truck parking locations are in ${stateRecord.stateName}?`, [
        countBullet,
        shortcutBullet,
        cityBullet,
      ]),
      buildBulletSection(`Which highways serve these parking locations in ${stateRecord.stateName}?`, [
        highways.length > 0 ? `Highways near ${isSingle ? 'this location' : 'these locations'} include ${formatList(highways)}.` : '',
        freightHotspots.length > 0 ? `Nearby freight and industrial areas include ${formatList(freightHotspots)}.` : '',
      ]),
      buildLinkSection(
        `${cityCount === 1 ? 'City' : 'Cities'} in ${stateRecord.stateName}`,
        stateRecord.cities.map((city) => ({
          path: buildTruckParkingCityPath({ stateSlug: stateRecord.stateSlug, citySlug: city.citySlug }),
          label: `${city.cityName}, ${stateRecord.stateCode}`,
          meta: `${pluralize(city.locations.length, 'location')} · ${pluralize(city.availableIntentSlugs.length, 'filter')}`,
        })),
      ),
      buildLinkSection('Helpful Shortcuts', buildStateIntentLinks(stateRecord, 6)),
      buildFaqSection('Driver Questions', STATE_FAQ_OVERRIDES[stateRecord.stateSlug] || [
        {
          question: `How do I find truck parking in ${stateRecord.stateName}?`,
          answer: isSingle
            ? `Open the lot page directly from this state page. Rates, amenities, and booking are all on the lot detail page.`
            : `Start with this state page, choose the city that matches your route, and then open Overnight, Secure, Monthly, or Near Highway results if you want a shorter list.`,
        },
        {
          question: `What will I find on this page in ${stateRecord.stateName}?`,
          answer: faqAnswer,
        },
      ]),
    ],
    featuredLocationsTitle: `Featured Locations in ${stateRecord.stateName}`,
    featuredLocations: buildFeaturedLocationItems(stateRecord.locations, 6),
    faqItems: [],
  };
};

const buildCityContent = ({ dataset = null, routeMeta = null } = {}) => {
  const cityRecord = getCityDiscoveryRecord(dataset, routeMeta?.stateSlug || '', routeMeta?.citySlug || '');
  if (!cityRecord || cityRecord.locations.length === 0) return null;

  const highways = collectTopHighways(cityRecord.locations, 4);
  const freightHotspots = collectTopFreightHotspots(cityRecord.locations, 4);
  const amenityLabels = collectTopLabels(cityRecord.locations, (location) => location?.profile?.amenityLabels, 5);
  const securityLabels = collectTopLabels(cityRecord.locations, (location) => location?.profile?.securityFeatures, 5);
  const lotFaqs = collectLotFaqs(cityRecord.locations, 6);

  const lotCount = cityRecord.locations.length;
  const isSingle = lotCount === 1;
  const lotsWord = isSingle ? 'lot' : 'lots';
  const thisThese = isSingle ? 'this lot' : 'these lots';

  const highwaySentence = highways.length > 0
    ? `${isSingle ? 'This lot is' : 'These lots are'} positioned near ${formatList(highways)} and ${isSingle ? 'serves' : 'serve'} commercial drivers moving through the area.`
    : `${isSingle ? 'This lot serves' : 'These lots serve'} commercial drivers moving through ${cityRecord.cityName} and surrounding areas.`;
  const intro = isSingle
    ? [
        `Truck parking in ${cityRecord.cityName}, ${cityRecord.stateName} is available at ${cityRecord.locations[0]?.profile?.areaLabel || cityRecord.locations[0]?.title || 'this location'}, operated by Truck Parking Hub.`,
        highwaySentence,
        'Online booking is available 24/7, with daily, weekly, and monthly rate options.',
      ].join(' ')
    : [
        `Truck parking in ${cityRecord.cityName}, ${cityRecord.stateName} is available across ${pluralize(lotCount, 'location')} operated by Truck Parking Hub.`,
        highwaySentence,
        'Each location supports online booking 24/7, with daily, weekly, and monthly rate options available.',
      ].join(' ');

  const lotSummaries = sortFeaturedLocations(cityRecord.locations, 6)
    .map((location) => {
      const lotIntro = location.profile?.intro || '';
      if (!lotIntro) return null;
      const label = location.title || location.address || '';
      return label ? `${label}: ${lotIntro}` : lotIntro;
    })
    .filter(Boolean);

  const faqItems = CITY_FAQ_OVERRIDES[cityRecord.citySlug]
    || (lotFaqs.length > 0 ? lotFaqs : isSingle
      ? [
          {
            question: `What parking is available in ${cityRecord.cityName}?`,
            answer: `Truck Parking Hub operates 1 active lot in ${cityRecord.cityName}, ${cityRecord.stateName}. Open the lot page to view rates, amenities, access details, and book online.`,
          },
          {
            question: `How do I book the parking lot in ${cityRecord.cityName}?`,
            answer: 'Select this location, choose your duration, and complete checkout online. Your confirmation includes gate code access and arrival instructions.',
          },
        ]
      : [
          {
            question: `How do I compare parking options in ${cityRecord.cityName}?`,
            answer: 'Start with this city page, use the Overnight, Secure, Monthly, or Near Highway links if you want a shorter list, and then open the lot you want to book.',
          },
          {
            question: `How can I narrow the list in ${cityRecord.cityName}?`,
            answer: `You can narrow this city list with ${pluralize(cityRecord.availableIntentSlugs.length, 'filter')}, including Overnight, Secure, Monthly, and Near Highway when those options are available.`,
          },
        ]);

  const localContext = CITY_LOCAL_CONTEXT[cityRecord.citySlug] || null;

  const corridorSlugs = CITY_CORRIDOR_LINKS[cityRecord.citySlug] || [];
  const corridorLinks = corridorSlugs
    .map((slug) => {
      const record = getCityDiscoveryRecord(dataset, cityRecord.stateSlug, slug);
      if (!record) return null;
      return {
        path: buildTruckParkingCityPath({ stateSlug: cityRecord.stateSlug, citySlug: slug }),
        label: `${record.cityName}, ${cityRecord.stateCode}`,
        meta: `${pluralize(record.locations.length, 'location')}`,
      };
    })
    .filter(Boolean);

  const highwayLinks = (CITY_HIGHWAY_LINKS[cityRecord.citySlug] || []).map(({ slug, label }) => ({
    path: `/truck-parking/${slug}`,
    label,
    meta: 'Highway corridor page',
  }));

  return {
    routeKind: 'city',
    heading: `Truck Parking in ${cityRecord.cityName}, ${cityRecord.stateName}`,
    sectionHeading: `Parking in ${cityRecord.cityName}`,
    intro,
    statItems: [
      pluralize(cityRecord.locations.length, 'parking location'),
      pluralize(cityRecord.availableIntentSlugs.length, 'filter'),
      pluralize(countLocationsWithIntent(cityRecord.locations, 'secure'), 'secure option'),
    ],
    sections: [
      ...(lotSummaries.length > 0 ? [buildParagraphSection(isSingle ? `Parking Lot in ${cityRecord.cityName}` : `Parking Lots in ${cityRecord.cityName}`, lotSummaries)] : []),
      ...(localContext ? [buildParagraphSection(localContext.title, localContext.paragraphs)] : []),
      buildBulletSection(`What truck parking options are available in ${cityRecord.cityName}?`, [
        amenityLabels.length > 0 ? `Amenities at ${thisThese} include ${formatList(amenityLabels)}.` : `You can review ${lotsWord} details, amenities, and direct booking links from this page.`,
        securityLabels.length > 0 ? `Security features at ${thisThese} include ${formatList(securityLabels)}.` : 'Security details appear on each lot page when the location provides them.',
        !isSingle ? 'Use the links below if you only want Overnight, Secure, Monthly, or Near Highway parking.' : null,
      ]),
      buildBulletSection(`Which highways are near ${cityRecord.cityName} truck parking?`, [
        highways.length > 0 ? `Highways near ${thisThese} include ${formatList(highways)}.` : '',
        freightHotspots.length > 0 ? `Nearby freight and industrial areas include ${formatList(freightHotspots)}.` : '',
      ]),
      buildLinkSection(`Quick Filters in ${cityRecord.cityName}`, buildCityIntentLinks(cityRecord), 'More filters will show here as more parking locations are added.'),
      ...(corridorLinks.length > 0 ? [buildLinkSection('Other Parking on This Corridor', corridorLinks)] : []),
      ...(highwayLinks.length > 0 ? [buildLinkSection('Highway Corridor Pages', highwayLinks)] : []),
      buildFaqSection('Driver Questions', faqItems),
    ],
    featuredLocationsTitle: isSingle ? `Parking Location in ${cityRecord.cityName}` : `Parking Locations in ${cityRecord.cityName}`,
    featuredLocations: buildFeaturedLocationItems(cityRecord.locations, 6),
    faqItems: [],
  };
};

const buildIntentPurposeItems = (intentRecord = null) => {
  const intentSlug = String(intentRecord?.intentSlug || '').trim().toLowerCase();
  const cityName = intentRecord?.city?.cityName || 'this city';

  if (intentSlug === 'overnight') {
    return [
      `Use this page in ${cityName} when you need a same-day or overnight stop.`,
      'It keeps the list focused on lots that support daily parking.',
    ];
  }

  if (intentSlug === 'secure') {
    return [
      `Use this page in ${cityName} when gate access, fencing, lighting, or cameras matter most.`,
      'It keeps the list focused on lots with stronger security details.',
    ];
  }

  if (intentSlug === 'monthly') {
    return [
      `Use this page in ${cityName} if you need longer-stay parking for a repeat lane, local run, or fleet vehicle.`,
      'It keeps the list focused on lots that support monthly parking.',
    ];
  }

  if (intentSlug.startsWith('near-')) {
    return [
      `Use this page when you want parking closer to the highway named here instead of scanning every lot in ${cityName}.`,
      'It helps you focus on lots near that route.',
    ];
  }

  return [
    `Use this page when you want a smaller list of locations in ${cityName} that fit a specific parking need.`,
  ];
};

const buildIntentContent = ({ dataset = null, routeMeta = null } = {}) => {
  const intentRecord = getIntentDiscoveryRecord(
    dataset,
    routeMeta?.stateSlug || '',
    routeMeta?.citySlug || '',
    routeMeta?.intentSlug || '',
  );
  if (!intentRecord || intentRecord.locations.length === 0) return null;

  const cityRecord = intentRecord.city;
  const amenityLabels = collectTopLabels(intentRecord.locations, (location) => location?.profile?.amenityLabels, 5);
  const securityLabels = collectTopLabels(intentRecord.locations, (location) => location?.profile?.securityFeatures, 5);
  const highways = collectTopHighways(intentRecord.locations, 4);

  return {
    routeKind: 'intent',
    heading: `${intentRecord.label} in ${cityRecord.cityName}, ${cityRecord.stateName}`,
    sectionHeading: `${intentRecord.label} Guide`,
    intro: `${pluralize(intentRecord.locations.length, 'location')} in ${cityRecord.cityName}, ${cityRecord.stateName} match this filter. Open any lot for rates and details.`,
    statItems: [
      pluralize(intentRecord.locations.length, 'matching location'),
      pluralize(buildIntentSiblingLinks(intentRecord).length, 'other filter'),
      pluralize(cityRecord.locations.length, 'location in the city', 'locations in the city'),
    ],
    sections: [
      buildBulletSection(`Why do drivers look for ${intentRecord.label.toLowerCase()} in ${cityRecord.cityName}?`, buildIntentPurposeItems(intentRecord)),
      buildBulletSection(`What do these ${intentRecord.label.toLowerCase()} lots offer in ${cityRecord.cityName}?`, [
        highways.length > 0 ? `Highways near these lots include ${formatList(highways)}.` : '',
        securityLabels.length > 0 ? `Security features on these lots include ${formatList(securityLabels)}.` : '',
        amenityLabels.length > 0 ? `Amenities at these lots include ${formatList(amenityLabels)}.` : '',
      ]),
      buildLinkSection(`Other Parking Options in ${cityRecord.cityName}`, buildIntentSiblingLinks(intentRecord), 'More filters will show here as more parking locations are added.'),
      buildLinkSection('Back to Broader Results', [
        {
          path: buildTruckParkingStatePath(cityRecord.stateSlug),
          label: `${cityRecord.stateName} Truck Parking`,
          meta: 'Back to the statewide page',
        },
        {
          path: buildTruckParkingCityPath({ stateSlug: cityRecord.stateSlug, citySlug: cityRecord.citySlug }),
          label: `${cityRecord.cityName} Truck Parking`,
          meta: 'Back to the city page',
        },
      ]),
      buildFaqSection('Driver Questions', [
        {
          question: `How many locations fit this search in ${cityRecord.cityName}?`,
          answer: `${pluralize(intentRecord.locations.length, 'location')} fit this page right now, and each one links straight to its lot page with pricing and booking details.`,
        },
        {
          question: `Can I compare other parking options in ${cityRecord.cityName}?`,
          answer: `Yes. Use the links above to switch between other Overnight, Secure, Monthly, or Near Highway options in ${cityRecord.cityName}.`,
        },
      ]),
    ],
    featuredLocationsTitle: 'Matching Locations',
    featuredLocations: buildFeaturedLocationItems(intentRecord.locations, 6),
    faqItems: [],
  };
};

export const buildTruckParkingHomepageContent = ({ dataset = null } = {}) => buildHomepageContent({ dataset });

export const buildTruckParkingRouteContent = ({ dataset = null, routeMeta = null } = {}) => {
  if (!routeMeta?.isTruckParkingRoute) return null;

  if (routeMeta.routeType === 'hub') return buildHubContent({ dataset });
  if (routeMeta.routeType === 'state') return buildStateContent({ dataset, routeMeta });
  if (routeMeta.routeType === 'city') return buildCityContent({ dataset, routeMeta });
  if (routeMeta.routeType === 'intent') return buildIntentContent({ dataset, routeMeta });
  return null;
};

export default {
  buildTruckParkingHomepageContent,
  buildTruckParkingRouteContent,
};
